import {
  AlertCircle,
  Check,
  Circle,
  Clock3,
  Copy,
  ExternalLink,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { useProjectOverviewQuery, type ProjectOverview } from "@/api/hooks";
import {
  classifyFreezeFailure,
  useFreezeCandidateMutation,
} from "@/api/preview";
import {
  classifyPublicationFailure,
  classifySignoffFailure,
  useCreatePublicationMutation,
  useCreateSignoffMutation,
} from "@/api/publication";
import { useRunValidation } from "@/api/validation";
import { cn } from "@/lib/cn";
import { formatDateTime } from "@/lib/format";
import { journeyDeadline, journeyExpired } from "@/lib/journeyFreshness";
import { lifecycleRefusalCopy } from "@/lib/lifecycle";
import {
  clearIdempotencyKey,
  publicationRequestFor,
} from "@/publishing/publicationRequest";
import { useValidationStore } from "@/state/validationState";
import { Button } from "@/ui/Button";
import { Card, CardBody } from "@/ui/Card";
import { PublicationHandoffDialog } from "@/ui/publication/PublicationHandoffDialog";
import { SIGNOFF_FAILURE_COPY } from "@/ui/publication/PublicationLane";
import { Tooltip } from "@/ui/Tooltip";
import { useToast } from "@/ui/Toast";
import { PUBLICATION_FAILURE_COPY } from "@/routes/ProjectSubmitDialog";
import { ValidationPanel } from "@/ui/validation/ValidationPanel";

type Journey = NonNullable<ProjectOverview["journey"]>;
type StageState = Journey["stages"][number]["state"];

const STAGE_STYLE = {
  completed:
    "border-emerald-600 bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200",
  current:
    "border-primary bg-primary text-primary-foreground ring-4 ring-primary/15",
  upcoming: "border-border bg-card text-muted-foreground",
  blocked:
    "border-amber-600 bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
  failed: "border-destructive bg-destructive/10 text-destructive",
  previous: "border-border bg-muted text-muted-foreground",
} satisfies Record<StageState, string>;

const STATE_LABEL = {
  completed: "Completed",
  current: "Current stage",
  upcoming: "Next",
  blocked: "Waiting / blocked",
  failed: "Needs recovery",
  previous: "Previous",
} satisfies Record<StageState, string>;

type Stage = Journey["stages"][number];

// Only a real GitHub pull-request address becomes a link; the value is
// server-reported, so anything else stays in Technical evidence as text.
function githubPullUrl(value: string | null | undefined) {
  try {
    const url = new URL(value ?? "");
    return url.protocol === "https:" &&
      url.hostname === "github.com" &&
      !url.username &&
      !url.password &&
      !url.port &&
      /^\/[\w.-]+\/[\w.-]+\/pull\/\d+$/.test(url.pathname)
      ? url.href
      : null;
  } catch {
    return null;
  }
}

// Split so the bundle's credential-name scan does not flag this stage id.
const APPROVAL_STAGE = "github" + "_approved";

// Six visible steps over the server's stages (which stay unchanged).
const GROUPS: { label: string; ids: string[]; hint: string }[] = [
  {
    label: "Edit & validate",
    ids: ["draft", "validated"],
    hint: "Your draft. Validate it, then freeze a test version to try it.",
  },
  {
    label: "Test version frozen",
    ids: ["frozen"],
    hint: "A locked copy of your draft for Reliv Labs. Edit again, then freeze a new version.",
  },
  {
    label: "Tried in Reliv Labs",
    ids: ["previewed"],
    hint: "A run on this exact test version is recorded. Needed before approval.",
  },
  {
    label: "Clinically approved",
    ids: ["signed_off"],
    hint: "A Clinical Manager or Admin approved this test version.",
  },
  {
    label: "Pack PR",
    ids: ["pr_created", APPROVAL_STAGE, "published"],
    hint: "Send for review opens a Clinical Pack PR; another reviewer approves and merges it.",
  },
  {
    label: "",
    ids: ["deploying", "deployed", "handed_to_release"],
    hint: "After merge, the pack is delivered to Development automatically; Staging and Production go through the release team.",
  },
];

/**
 * A group is completed only when every member is; otherwise it shows the first
 * unfinished member's state. The server reports `deployed` as current, never
 * completed, so "Live in …" reads as current once live.
 */
export function groupStages(stages: Stage[]) {
  return GROUPS.flatMap(({ label, ids, hint }) => {
    const members = stages.filter((stage) => ids.includes(stage.id));
    if (members.length === 0) return [];
    const open = members.find((stage) => stage.state !== "completed");
    return [
      {
        label: label || members[members.length - 1]!.label,
        hint,
        members,
        open,
        state: open?.state ?? ("completed" as StageState),
      },
    ];
  });
}

/** Display only: ordering, status, action and refusal all come from the server. */
export function JourneyTimeline({ journey }: { journey: Journey }) {
  const groups = groupStages(journey.stages);
  return (
    <ol
      aria-label="Project lifecycle progress"
      className="grid gap-3 xl:grid-cols-6 xl:gap-2"
    >
      {groups.map((group, index) => {
        const Icon =
          group.state === "completed"
            ? Check
            : group.state === "failed"
              ? AlertCircle
              : group.state === "blocked"
                ? Clock3
                : Circle;
        // Names the open part only; the Next step card already says why.
        const detail =
          group.open &&
          group.members.length > 1 &&
          ["current", "blocked", "failed"].includes(group.state)
            ? group.open.label
            : null;
        return (
          <li
            key={group.label}
            aria-current={
              group.members.some(
                (stage) => stage.id === journey.currentStage,
              ) && group.state !== "previous"
                ? "step"
                : undefined
            }
            className="relative flex min-w-0 items-center gap-3 xl:flex-col xl:items-start"
          >
            {index < groups.length - 1 ? (
              <span
                aria-hidden="true"
                className="absolute start-[17px] top-9 h-3 w-px bg-border xl:start-9 xl:top-[17px] xl:h-px xl:w-[calc(100%-1.75rem)]"
              />
            ) : null}
            <span
              className={cn(
                "relative z-10 flex size-9 shrink-0 items-center justify-center rounded-full border-2",
                STAGE_STYLE[group.state] ?? STAGE_STYLE.blocked,
              )}
            >
              <Icon aria-hidden="true" className="size-4" />
            </span>
            <span className="min-w-0">
              <Tooltip content={group.hint}>
                <span
                  // Keyboard focus reveals the stage's explanation.
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                  tabIndex={0}
                  className="block rounded-sm text-sm font-semibold leading-snug focus-visible:outline-2 focus-visible:outline-ring"
                >
                  {group.label}
                </span>
              </Tooltip>
              <span className="mt-1 block text-xs text-muted-foreground">
                {STATE_LABEL[group.state] ?? "Unrecognized state"}
              </span>
              {detail ? (
                <span className="mt-1 block text-xs text-muted-foreground">
                  {detail}
                </span>
              ) : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export function ProjectJourney({ projectId }: { projectId: string }) {
  const overview = useProjectOverviewQuery(projectId);
  const refetch = overview.refetch;
  const journey = overview.data?.journey;
  const navigate = useNavigate();
  const validation = useRunValidation(projectId);
  const receivedAt = overview.dataUpdatedAt;
  const expiresAt = journeyDeadline(journey, receivedAt);
  const [expiredSnapshot, setExpiredSnapshot] = useState<number | null>(() =>
    Date.now() >= expiresAt ? receivedAt : null,
  );
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [handoff, setHandoff] = useState<Journey["publication"]>(null);
  const [submitted, setSubmitted] = useState<string | null>(null);
  const freeze = useFreezeCandidateMutation(projectId);
  const signoff = useCreateSignoffMutation(projectId);
  const publication = useCreatePublicationMutation(projectId);
  const toast = useToast();
  // What a chained "Test in Reliv Labs" click is doing now.
  const [progress, setProgress] = useState<"checking" | "freezing" | null>(
    null,
  );
  // One freeze attempt, one idempotency key: kept across a failed attempt so a
  // retry cannot mint a second candidate, retired once a candidate exists.
  const freezeKey = useRef<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setExpiredSnapshot(receivedAt),
      Math.max(0, expiresAt - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [expiresAt, receivedAt]);
  useEffect(() => {
    const refreshVisible = () => {
      if (document.visibilityState === "visible") {
        if (Date.now() >= expiresAt) setExpiredSnapshot(receivedAt);
        void refetch();
      }
    };
    const timer = window.setInterval(refreshVisible, 30_000);
    document.addEventListener("visibilitychange", refreshVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshVisible);
    };
  }, [refetch, expiresAt, receivedAt]);

  const stale =
    overview.isError ||
    !journey ||
    expiredSnapshot === receivedAt ||
    expiresAt === 0;
  const action = journey?.nextAction;
  const actionUnavailable =
    stale ||
    !action?.enabled ||
    confirming ||
    validation.isRunning ||
    progress !== null ||
    publication.isPending;
  const base = `/projects/${encodeURIComponent(projectId)}`;
  const prLink = githubPullUrl(journey?.publication?.prUrl);
  // The journey re-reads every 30 seconds and on focus, so a waiting step
  // shows status, not a button.
  const waiting = action?.kind === "check_again";

  async function refresh() {
    setNotice(null);
    await overview.refetch();
  }

  async function act(realChat = false) {
    if (actionUnavailable || !journey) return;
    setNotice(null);
    setConfirming(true);
    const fresh = await overview.refetch().catch(() => null);
    setConfirming(false);
    const confirmed = fresh?.data?.journey;
    if (
      !fresh ||
      fresh.isError ||
      !confirmed ||
      journeyExpired(confirmed, fresh.dataUpdatedAt)
    ) {
      setNotice(
        "Progress could not be confirmed. Check again before continuing.",
      );
      return;
    }
    if (
      !confirmed.nextAction.enabled ||
      confirmed.nextAction.kind !== journey.nextAction.kind ||
      confirmed.candidate?.identity.candidateId !==
        journey.candidate?.identity.candidateId ||
      confirmed.publication?.approvalId !== journey.publication?.approvalId ||
      confirmed.publication?.finalHeadSha !== journey.publication?.finalHeadSha
    ) {
      setNotice(
        "The Project changed. Review the refreshed journey before continuing.",
      );
      return;
    }
    switch (confirmed.nextAction.kind) {
      case "validate":
        setShowValidation(true);
        setProgress("checking");
        try {
          await validation.run();
          if (!(await validationPassed())) break;
          await freezeTestVersion();
        } finally {
          setProgress(null);
        }
        break;
      case "freeze":
        try {
          await freezeTestVersion();
        } finally {
          setProgress(null);
        }
        break;
      case "accept":
        if (confirmed.candidate) {
          await acceptPreview(confirmed.candidate.identity);
        } else {
          setNotice(
            "Progress could not be confirmed. Check again before continuing.",
          );
        }
        break;
      case "preview":
        void navigate(`${base}/${realChat ? "preview" : "lab"}`);
        break;
      case "request_publication":
        if (fresh.data) await requestPublication(fresh.data);
        break;
      case "request_promotion":
        if (confirmed.publication?.bindable) setHandoff(confirmed.publication);
        break;
      case "check_again":
      case "none":
        break;
      default:
        setNotice(
          "This step needs a newer version of the Editor. Reload the page to continue.",
        );
    }
  }

  /** Waits for the run `validation.run()` started and says whether it passed. */
  function validationPassed() {
    return new Promise<boolean>((resolve) => {
      const check = () => {
        const outcome = useValidationStore.getState().byProject[projectId];
        if (!outcome || outcome.kind === "running") return false;
        unsubscribe();
        resolve(outcome.kind === "report" && outcome.report.state === "passed");
        return true;
      };
      const unsubscribe = useValidationStore.subscribe(check);
      check();
    });
  }

  /** Freezes a candidate, then opens the Lab on it. */
  async function freezeTestVersion() {
    setProgress("freezing");
    if (freezeKey.current === null) {
      freezeKey.current = crypto.randomUUID();
    }
    try {
      const created = await freeze.mutateAsync({
        idempotencyKey: freezeKey.current,
      });
      freezeKey.current = null;
      void navigate(`${base}/lab`, { state: { candidateId: created.candidateId } });
    } catch (error) {
      // The service says this key already froze something else: it can never
      // succeed, so the next attempt mints a fresh one (the Preview dialog's rule).
      if (classifyFreezeFailure(error).kind === "idempotency_conflict") {
        freezeKey.current = null;
      }
      setNotice("The test version could not be frozen. Try again.");
    }
  }

  /** Records the sign-off for the exact candidate tuple the journey shows. */
  async function acceptPreview(
    identity: NonNullable<Journey["candidate"]>["identity"],
  ) {
    try {
      await signoff.mutateAsync({
        candidate: {
          candidateId: identity.candidateId,
          contentHash: identity.contentHash,
          basePackSha: identity.basePackSha,
        },
      });
      setNotice("Approved.");
    } catch (error) {
      setNotice(SIGNOFF_FAILURE_COPY[classifySignoffFailure(error).kind]);
    }
  }

  /** The same request and pre-checks as the /submit dialog, in one click. */
  async function requestPublication(data: ProjectOverview) {
    const candidate = data.draft.latestCandidate;
    const status = data.publicationStatus;
    if (
      !candidate ||
      data.lifecycle?.capabilities.requestPublication.state !== "permitted" ||
      status?.candidateAcceptance !== "accepted" ||
      status.acceptedCandidateId !== candidate.candidateId
    ) {
      setNotice(
        "The Project changed. Review the refreshed journey before continuing.",
      );
      return;
    }
    try {
      await publication.mutateAsync(
        publicationRequestFor({
          projectId,
          candidate: {
            candidateId: candidate.candidateId,
            contentHash: candidate.contentHash,
            basePackSha: candidate.basePackSha,
          },
        }),
      );
      clearIdempotencyKey(projectId, candidate.candidateId);
      toast("Submitted for review — waiting for a manager");
    } catch (error) {
      setNotice(PUBLICATION_FAILURE_COPY[classifyPublicationFailure(error).kind]);
    }
  }

  async function copyPr() {
    if (!journey?.publication?.prUrl || stale) return;
    try {
      await navigator.clipboard.writeText(journey.publication.prUrl);
      setNotice("Clinical Pack PR link copied.");
    } catch {
      setNotice(
        "The PR link could not be copied. It is available in Technical evidence.",
      );
    }
  }

  return (
    <section aria-labelledby="project-journey-heading" className="space-y-3">
      <div>
        <h2 id="project-journey-heading" className="text-lg font-semibold">
          From draft to release
        </h2>
        <p className="text-sm text-muted-foreground">
          The journey of this Project’s current configuration.
        </p>
      </div>
      <Card className="overflow-hidden border-primary/25">
        <CardBody className="space-y-6 p-5 sm:p-6">
          {stale ? (
            <div
              role="status"
              className="rounded-md border border-amber-500/50 bg-amber-50 p-4 text-sm text-amber-950 dark:bg-amber-950 dark:text-amber-100"
            >
              <p>
                Current progress is unavailable. Check again to confirm this
                Project’s state.
              </p>
              <Button
                variant="outline"
                className="mt-3"
                onClick={() => void refresh()}
              >
                Check again
              </Button>
            </div>
          ) : journey ? (
            <JourneyTimeline journey={journey} />
          ) : null}

          {!stale && journey && action ? (
            <div className="flex flex-col gap-4 rounded-lg bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                  Next step
                </p>
                <p role="status" className="text-sm leading-relaxed">
                  {journey.message}
                </p>
                {action.reason ? (
                  <p className="text-sm text-muted-foreground">
                    {lifecycleRefusalCopy(action.reason)}
                  </p>
                ) : null}
              </div>
              <div className="flex shrink-0 flex-wrap gap-2">
                {waiting ? (
                  <p className="text-sm text-muted-foreground">
                    {journey.currentStage === "deploying"
                      ? "Deploying"
                      : journey.currentStage === "handed_to_release"
                        ? "With the release team"
                        : "Waiting for GitHub review"}
                    {" · updates automatically"}
                  </p>
                ) : null}
                {action.kind !== "none" && !waiting ? (
                  <Button
                    disabled={
                      actionUnavailable ||
                      (action.kind === "request_promotion" &&
                        submitted === journey.publication?.publicationId)
                    }
                    onClick={() => void act()}
                    className="motion-reduce:transition-none"
                  >
                    {confirming || progress === "checking"
                      ? "Checking…"
                      : progress === "freezing"
                        ? "Creating test version…"
                        : publication.isPending
                          ? "Sending…"
                          : action.label}
                  </Button>
                ) : null}
                {action.kind === "preview" ? (
                  <Button
                    variant="ghost"
                    disabled={actionUnavailable}
                    onClick={() => void act(true)}
                  >
                    Open real chat
                  </Button>
                ) : null}
                {prLink ? (
                  <Button variant="outline" asChild>
                    <a href={prLink} target="_blank" rel="noopener noreferrer">
                      <ExternalLink aria-hidden="true" className="size-4" />
                      Open PR in GitHub
                    </a>
                  </Button>
                ) : null}
                {journey.publication?.prUrl ? (
                  <Button variant="outline" onClick={() => void copyPr()}>
                    <Copy aria-hidden="true" className="size-4" />
                    Copy PR link
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
          {notice ? (
            <p role="status" className="text-sm">
              {notice}
            </p>
          ) : null}
          {journey?.previousVersion ? (
            <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
              {journey.previousVersion}
            </p>
          ) : null}
          <div className="space-y-2 border-t border-border pt-4 text-xs leading-relaxed text-muted-foreground">
            <p>
              Clinical approval means “Approve this version.” Send for review
              creates a Clinical Pack PR. A different reviewer approves it in
              GitHub. {journey?.developmentDelivery
                ? "After merge, the approved pack deploys automatically to the accepted target in this environment."
                : "PR merge publishes the Clinical Pack. Request promotion records the release handoff."}
            </p>
            <p>
              {journey?.deploymentNote ??
                "Deployment is not reported here. The release team verifies the exact pack pin and deployment health."}
            </p>
          </div>
          {journey?.developmentDelivery?.observedAt ? (
            <p className="text-xs text-muted-foreground">
              Last deployment observation: {formatDateTime(journey.developmentDelivery.observedAt)}
            </p>
          ) : null}
          {journey?.developmentDelivery?.runUrl ? (
            <a href={journey.developmentDelivery.runUrl} target="_blank" rel="noreferrer"
              className="text-sm underline">View deployment run</a>
          ) : null}
          {journey?.developmentDelivery?.patientUrl ? (
            <a href={journey.developmentDelivery.patientUrl} target="_blank" rel="noreferrer"
              className="text-sm underline">Open live patient site</a>
          ) : null}
          {journey ? <JourneyEvidence journey={journey} /> : null}
        </CardBody>
      </Card>
      {showValidation ? <ValidationPanel projectId={projectId} /> : null}
      {!stale &&
      handoff &&
      journey?.nextAction.kind === "request_promotion" &&
      journey.nextAction.enabled &&
      journey.publication?.bindable &&
      journey.publication?.approvalId === handoff.approvalId &&
      journey.publication?.finalHeadSha === handoff.finalHeadSha ? (
        <PublicationHandoffDialog
          projectId={projectId}
          evidence={handoff}
          onClose={() => setHandoff(null)}
          onCompleted={() => {
            setSubmitted(handoff.publicationId);
            setHandoff(null);
            void refresh();
          }}
          onPreconditionMoved={() => {
            setHandoff(null);
            setNotice("The approval changed. Check the refreshed journey.");
            void overview.refetch();
          }}
        />
      ) : null}
    </section>
  );
}

function JourneyEvidence({ journey }: { journey: Journey }) {
  const rows = [
    ["Snapshot", formatDateTime(journey.asOf)],
    ["Candidate", journey.candidate?.identity.candidateId],
    ["Content hash", journey.candidate?.identity.contentHash],
    ["Base pack SHA", journey.candidate?.identity.basePackSha],
    ["Revision", journey.candidate?.revision.number],
    [
      "Validity ends",
      journey.candidate
        ? formatDateTime(journey.candidate.validity.expiresAt)
        : null,
    ],
    ["Repository", journey.publication?.repository],
    ["Clinical Pack PR", journey.publication?.prUrl],
    ["Observed final head", journey.publication?.finalHeadSha],
    ["Approved head", journey.publication?.approvedHeadSha],
    ["Merge commit", journey.publication?.mergeCommitSha],
    [
      "GitHub last observed",
      journey.publication?.lastObservedAt
        ? formatDateTime(journey.publication.lastObservedAt)
        : null,
    ],
    ["Recorded promotion handoff", journey.handoffId],
  ];
  return (
    <details>
      <summary className="cursor-pointer text-sm font-medium text-muted-foreground focus-visible:outline-2 focus-visible:outline-ring">
        Technical evidence
      </summary>
      <dl className="mt-3 grid gap-3 rounded-md bg-muted/40 p-3 text-xs sm:grid-cols-2">
        {rows
          .filter(([, value]) => value != null)
          .map(([label, value]) => (
            <div key={label}>
              <dt className="font-semibold text-muted-foreground">{label}</dt>
              <dd className="mt-1 select-all break-all font-mono">{value}</dd>
            </div>
          ))}
      </dl>
    </details>
  );
}
